import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Droplet, AlertTriangle, Clock, ArrowUp, ArrowDown, Calendar, ShieldAlert, Eye } from 'lucide-react';
import { SlipEvent } from '../BathroomSafety';

// Mock data generation for slip events
const generateMockSlipEvents = (): SlipEvent[] => {
  const events: SlipEvent[] = [];
  const now = new Date();
  
  // Generate data for the past 90 days
  for (let i = 0; i < 90; i++) {
    const day = new Date();
    day.setDate(now.getDate() - i);
    
    // Random chance of slip event (higher in more recent days to show "improvement")
    const eventChance = i < 30 ? 0.15 : i < 60 ? 0.25 : 0.35;
    
    if (Math.random() < eventChance) {
      // Random time during the day, with higher probability during morning/evening
      const hour = Math.random() < 0.7 ? 
                  (Math.random() < 0.5 ? Math.floor(Math.random() * 10) : 16 + Math.floor(Math.random() * 8)) : 
                  Math.floor(Math.random() * 24);
      const minute = Math.floor(Math.random() * 60);
      
      const timestamp = new Date(day);
      timestamp.setHours(hour, minute);
      
      // Severity distribution
      let severity: 'low' | 'medium' | 'high';
      const severityRoll = Math.random();
      if (severityRoll < 0.6) severity = 'low';
      else if (severityRoll < 0.9) severity = 'medium';
      else severity = 'high';
      
      // Response time between 1 sec and 2 minutes, higher severity gets faster response
      const baseResponseTime = severity === 'high' ? 10 : severity === 'medium' ? 30 : 60;
      const responseTime = baseResponseTime + Math.floor(Math.random() * baseResponseTime);
      
      // Water presence more common in high and medium severity events
      const waterPresent = Math.random() < (severity === 'high' ? 0.7 : severity === 'medium' ? 0.5 : 0.3);
      
      // Fall detection related to severity
      const fallDetected = Math.random() < (severity === 'high' ? 0.4 : severity === 'medium' ? 0.1 : 0.0);
      
      // User recovery inversely related to severity
      const userRecovered = Math.random() < (severity === 'high' ? 0.3 : severity === 'medium' ? 0.7 : 0.95);
      
      events.push({
        id: `slip-${i}`,
        timestamp,
        severity,
        responseTime,
        hasAnomalousPressure: Math.random() < 0.7,
        waterPresent,
        userRecovered,
        fallDetected
      });
    }
  }
  
  // Sort by timestamp descending (most recent first)
  return events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

const mockSlipEvents = generateMockSlipEvents();

// Prepare data for time of day distribution
const prepareTimeOfDayData = (events: SlipEvent[]) => {
  const timeSegments = [
    { name: 'Early Morning (12AM-6AM)', start: 0, end: 6, count: 0 },
    { name: 'Morning (6AM-12PM)', start: 6, end: 12, count: 0 },
    { name: 'Afternoon (12PM-6PM)', start: 12, end: 18, count: 0 },
    { name: 'Evening (6PM-12AM)', start: 18, end: 24, count: 0 }
  ];
  
  events.forEach(event => {
    const hour = event.timestamp.getHours();
    for (const segment of timeSegments) {
      if (hour >= segment.start && hour < segment.end) {
        segment.count++;
        break;
      }
    }
  });
  
  return timeSegments;
};

// Prepare data for severity distribution
const prepareSeverityData = (events: SlipEvent[]) => {
  const counts = { low: 0, medium: 0, high: 0 };
  
  events.forEach(event => {
    counts[event.severity]++;
  });
  
  return [
    { name: 'Low', value: counts.low, color: '#4ADE80' },
    { name: 'Medium', value: counts.medium, color: '#FBBF24' },
    { name: 'High', value: counts.high, color: '#EF4444' }
  ];
};

// Prepare data for monthly trends
const prepareMonthlyTrends = (events: SlipEvent[]) => {
  const monthData: { [key: string]: { total: number, withWater: number, falls: number } } = {};
  
  events.forEach(event => {
    const yearMonth = event.timestamp.toISOString().substring(0, 7); // YYYY-MM format
    
    if (!monthData[yearMonth]) {
      monthData[yearMonth] = { total: 0, withWater: 0, falls: 0 };
    }
    
    monthData[yearMonth].total++;
    if (event.waterPresent) monthData[yearMonth].withWater++;
    if (event.fallDetected) monthData[yearMonth].falls++;
  });
  
  // Convert to chart data format
  return Object.keys(monthData)
    .sort()
    .slice(-6) // Last 6 months
    .map(month => {
      const date = new Date(month + '-01');
      return {
        month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        total: monthData[month].total,
        withWater: monthData[month].withWater,
        falls: monthData[month].falls
      };
    });
};

// Calculate response time averages by severity
const calculateResponseTimeStats = (events: SlipEvent[]) => {
  const stats = { low: 0, medium: 0, high: 0, overall: 0 };
  const counts = { low: 0, medium: 0, high: 0, overall: 0 };
  
  events.forEach(event => {
    stats[event.severity] += event.responseTime;
    counts[event.severity]++;
    
    stats.overall += event.responseTime;
    counts.overall++;
  });
  
  // Convert to averages
  for (const key of Object.keys(stats) as Array<keyof typeof stats>) {
    if (counts[key] > 0) {
      stats[key] = Math.round(stats[key] / counts[key]);
    }
  }
  
  return stats;
};

// Calculate improvements in slip events over time
const calculateImprovements = (events: SlipEvent[]) => {
  if (events.length < 10) return { trend: 'neutral', percentage: 0 };
  
  // Compare last 30 days to previous 30 days
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const sixtyDaysAgo = new Date();
  sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
  
  const recentEvents = events.filter(e => e.timestamp >= thirtyDaysAgo);
  const previousEvents = events.filter(e => e.timestamp >= sixtyDaysAgo && e.timestamp < thirtyDaysAgo);
  
  if (previousEvents.length === 0) return { trend: 'neutral', percentage: 0 };
  
  const recentCount = recentEvents.length;
  const previousCount = previousEvents.length;
  
  const percentage = Math.round(((previousCount - recentCount) / previousCount) * 100);
  
  return {
    trend: percentage > 0 ? 'improving' : percentage < 0 ? 'worsening' : 'neutral',
    percentage: Math.abs(percentage)
  };
};

const SlipDetectionMonitoring: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'month' | 'quarter' | 'year'>('quarter');
  
  // Filter events based on time range
  const filteredEvents = mockSlipEvents.filter(event => {
    const now = new Date();
    const timeLimit = new Date();
    
    if (timeRange === 'month') {
      timeLimit.setDate(now.getDate() - 30);
    } else if (timeRange === 'quarter') {
      timeLimit.setDate(now.getDate() - 90);
    } else {
      timeLimit.setDate(now.getDate() - 365);
    }
    
    return event.timestamp >= timeLimit;
  });
  
  const timeOfDayData = prepareTimeOfDayData(filteredEvents);
  const severityData = prepareSeverityData(filteredEvents);
  const monthlyTrends = prepareMonthlyTrends(filteredEvents);
  const responseTimeStats = calculateResponseTimeStats(filteredEvents);
  const improvements = calculateImprovements(filteredEvents);
  
  // Calculate total events
  const totalEvents = filteredEvents.length;
  const eventsWithWater = filteredEvents.filter(e => e.waterPresent).length;
  const waterPercentage = totalEvents > 0 ? Math.round((eventsWithWater / totalEvents) * 100) : 0;
  
  // Get recent events for the list
  const recentEvents = filteredEvents.slice(0, 5);
  
  return (
    <div className="p-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-2">
          <button
            className={`px-4 py-2 rounded-lg ${
              timeRange === 'month' ? 'bg-cyan-100 text-cyan-800' : 'bg-gray-100 text-gray-800'
            }`}
            onClick={() => setTimeRange('month')}
          >
            Past Month
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              timeRange === 'quarter' ? 'bg-cyan-100 text-cyan-800' : 'bg-gray-100 text-gray-800'
            }`}
            onClick={() => setTimeRange('quarter')}
          >
            Past 3 Months
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              timeRange === 'year' ? 'bg-cyan-100 text-cyan-800' : 'bg-gray-100 text-gray-800'
            }`}
            onClick={() => setTimeRange('year')}
          >
            Past Year
          </button>
        </div>
        
        {improvements.trend !== 'neutral' && (
          <div className={`px-4 py-2 rounded-full text-sm font-medium flex items-center ${
            improvements.trend === 'improving' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {improvements.trend === 'improving' ? (
              <>
                <ArrowDown className="w-4 h-4 mr-1" />
                <span>{improvements.percentage}% fewer slip events</span>
              </>
            ) : (
              <>
                <ArrowUp className="w-4 h-4 mr-1" />
                <span>{improvements.percentage}% more slip events</span>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-cyan-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-cyan-900">Total Slip Events</h3>
              <p className="text-3xl font-bold text-cyan-700">{totalEvents}</p>
            </div>
            <AlertTriangle className="text-cyan-500" />
          </div>
          <p className="mt-2 text-sm text-cyan-600">
            During the past {timeRange === 'month' ? 'month' : timeRange === 'quarter' ? '3 months' : 'year'}
          </p>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-blue-900">Avg. Response Time</h3>
              <p className="text-3xl font-bold text-blue-700">{responseTimeStats.overall} sec</p>
            </div>
            <Clock className="text-blue-500" />
          </div>
          <p className="mt-2 text-sm text-blue-600">
            High severity: {responseTimeStats.high} sec | Medium: {responseTimeStats.medium} sec
          </p>
        </div>
        
        <div className="bg-teal-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-teal-900">Water Detection</h3>
              <p className="text-3xl font-bold text-teal-700">{waterPercentage}%</p>
            </div>
            <Droplet className="text-teal-500" />
          </div>
          <p className="mt-2 text-sm text-teal-600">
            {eventsWithWater} events involved water on the floor
          </p>
        </div>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4">Time of Day Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={timeOfDayData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" name="Slip Events" fill="#06B6D4" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <p className="mt-3 text-sm text-gray-600">
            Slip events are most common during {
              timeOfDayData.sort((a, b) => b.count - a.count)[0]?.name || 'N/A'
            }
          </p>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4">Severity Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={severityData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {severityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value} events`, 'Count']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Monthly Trends */}
      <div className="bg-white rounded-lg p-4 shadow-sm mb-6">
        <h3 className="text-lg font-medium mb-4">Monthly Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthlyTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="total" 
              name="All Slip Events" 
              stroke="#06B6D4" 
              strokeWidth={2} 
              activeDot={{ r: 8 }}
            />
            <Line 
              type="monotone" 
              dataKey="withWater" 
              name="Water-Related" 
              stroke="#0EA5E9" 
              strokeWidth={2} 
            />
            <Line 
              type="monotone" 
              dataKey="falls" 
              name="Falls" 
              stroke="#EF4444" 
              strokeWidth={2} 
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Recent Events and Safety Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg p-4 shadow-sm lg:col-span-2">
          <h3 className="text-lg font-medium mb-4">Recent Slip Events</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Response</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Water</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outcome</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentEvents.map((event) => (
                  <tr key={event.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {event.timestamp.toLocaleDateString()} {event.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        event.severity === 'high' 
                          ? 'bg-red-100 text-red-800' 
                          : event.severity === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {event.severity === 'high' && <AlertTriangle className="w-3 h-3 mr-1" />}
                        {event.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{event.responseTime} sec</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        event.waterPresent 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {event.waterPresent ? 'Detected' : 'None'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        event.fallDetected 
                          ? 'bg-red-100 text-red-800' 
                          : event.userRecovered
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {event.fallDetected ? 'Fall Detected' : event.userRecovered ? 'Self-Recovered' : 'Needed Assistance'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4 flex items-center">
            <ShieldAlert className="mr-2 text-cyan-600" />
            Safety Recommendations
          </h3>
          <div className="space-y-4">
            <div className="p-3 bg-cyan-50 rounded-lg">
              <h4 className="font-medium text-cyan-900 mb-1">Environmental Modifications</h4>
              <ul className="list-disc pl-5 text-sm text-cyan-800 space-y-1">
                <li>Install non-slip mats throughout bathroom</li>
                <li>Add grab bars near toilet and shower</li>
                <li>Ensure adequate lighting, especially at night</li>
                <li>Consider installing a raised toilet seat</li>
              </ul>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-1">Water Management</h4>
              <ul className="list-disc pl-5 text-sm text-blue-800 space-y-1">
                <li>Regularly check for bathroom leaks</li>
                <li>Use bath mats with rubber backing</li>
                <li>Install automatic water detection alerts</li>
                <li>Consider a walk-in shower with minimal threshold</li>
              </ul>
            </div>
            
            <div className="p-3 bg-teal-50 rounded-lg">
              <h4 className="font-medium text-teal-900 mb-1">Daily Habits</h4>
              <ul className="list-disc pl-5 text-sm text-teal-800 space-y-1">
                <li>Wipe up spills immediately</li>
                <li>Use shower chair if standing is difficult</li>
                <li>Take time transitioning in/out of bathtub</li>
                <li>Keep frequently used items within easy reach</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t">
            <button className="w-full flex items-center justify-center py-2 px-4 border border-cyan-300 rounded-md shadow-sm text-sm font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100">
              <Eye className="mr-2 w-4 h-4" />
              Schedule Safety Assessment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlipDetectionMonitoring; 